// Tournaments list

import { useState, useMemo } from 'react';
import { useFirebase } from './FirebaseContextProvider';
import moment from 'moment';
import { Link } from 'react-router-dom';
import Select from './Select';
import PaymentModalBody from './PaymentModalBody';
import PaymentInfo from './PaymentInfo';
import { useModal } from './ModalProvider';
import { UsersRoles, ParentStatuses, RefereeStatuses } from '../utils/enums';
import { formatGenders, formatAges, cityOf } from '../utils/misc';
import {
  useTeamPayments,
  useRefereeTournamentIds,
  useManagerTournamentIds,
  useEntities,
  useUser,
  useMyRefereeRoster,
} from '../utils/hooks';
import FilterInput from './FilterInput';
import DateRangeBadge from './DateRangeBadge';
import Spinner from './Spinner';
import placeholderTournament from '../assets/images/placeholder-tournament.png';
import './TournamentsList.scss';

const times = [
  'All time',
  ...(() => {
    const monthsList = [];
    for (let i = 0; i < 12; i++)
      monthsList.push(moment().add(i, 'months').format('MMMM YYYY'));
    return monthsList;
  })(),
];

export default function TournamentsList({
  teamId,
  userId,
  fieldManagerId,
  refereeId,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const {
    user,
    tournaments,
    teamsById,
    usersById,
    childrenByParentId,
    userIsAdmin,
    userIsCoach,
    userIsPlayer,
    userIsRefereeAssigner,
    userManagesReferees,
    isParentsLoaded,
  } = useFirebase();
  const { openModal } = useModal();

  // Only the team mode shows money, and only that mode reads it
  const paidByTournamentId = useTeamPayments(teamId);

  // Aggregate (userId) mode. Every test below is an allow-list, so an
  // unresolved subject would read as "invited to nothing"
  const { entity: subject, isResolved: isSubjectResolved } = useUser(userId);
  const subjectAdmittedOnly =
    subject?.role === UsersRoles.PLAYER || subject?.role === UsersRoles.PARENT;

  // A parent's teams are their approved children's. The provider registers
  // those documents for the parent themselves; an admin looking at somebody
  // else's card is not that parent, so the ids are registered here (P7 — keyed
  // on the joined string, not on the array).
  const childIdsKey = useMemo(() => {
    if (!userId || subject?.role !== UsersRoles.PARENT) return '';
    return (childrenByParentId.get(userId) ?? [])
      .filter((child) => child.status === ParentStatuses.APPROVED)
      .map((child) => child.id)
      .sort()
      .join('|');
  }, [userId, subject?.role, childrenByParentId]);
  const childIds = useMemo(
    () => (childIdsKey ? childIdsKey.split('|') : []),
    [childIdsKey],
  );
  const { isResolved: areChildrenResolved } = useEntities('users', childIds);

  const subjectTeamIds = useMemo(() => {
    if (!subject) return [];
    if (subject.role === UsersRoles.PARENT) {
      const ids = new Set();
      for (const childId of childIds) {
        for (const t of usersById.get(childId)?.teams ?? []) ids.add(t);
      }
      return Array.from(ids);
    }
    return subject.teams ?? [];
  }, [subject, childIds, usersById]);

  // Both remaining modes match a tournament's division against a team
  // document, and there is no organization-wide team list to read it off any
  // more — the ids are registered so the registry holds exactly them
  const lookedUpTeamIds = useMemo(
    () => (teamId ? [teamId] : subjectTeamIds),
    [teamId, subjectTeamIds],
  );
  const { isResolved: areLookedUpTeamsResolved } = useEntities(
    'teams',
    lookedUpTeamIds,
  );

  const locations = useMemo(
    () => [
      'All locations',
      ...new Set(
        tournaments.map((tourn) => cityOf(tourn.location)).filter(Boolean),
      ),
    ],
    [tournaments],
  );

  const sports = useMemo(
    () => [
      'All sports',
      ...new Set(tournaments.map((tournament) => tournament.sports).flat()),
    ],
    [tournaments],
  );

  const [location, setLocation] = useState(locations[0]);
  const [sport, setSport] = useState(sports[0]);
  const [time, setTime] = useState(times[0]);

  // The two modes that cannot be answered from the tournament document alone.
  // Both tests below are allow-lists, so an unfinished query reads as "involved
  // in nothing" and would empty the list — hence the flag rather than the ids.
  const { ids: refereedTournIds, isLoaded: areRefereedTournsLoaded } =
    useRefereeTournamentIds(refereeId);
  const { ids: managedTournIds, isLoaded: areManagedTournsLoaded } =
    useManagerTournamentIds(fieldManagerId);

  // A referee's own list adds the tournaments they accepted, games or not.
  // The query is theirs alone: nobody else viewing the card can run it
  const isOwnRefereeList = !!refereeId && refereeId === user?.id;
  const { entries: rosterEntries, isLoaded: isRosterLoaded } =
    useMyRefereeRoster();
  const acceptedTournIds = useMemo(
    () =>
      new Set(
        isOwnRefereeList
          ? rosterEntries
              .filter((e) => e.status === RefereeStatuses.ACCEPTED)
              .map((e) => e.tournamentId)
          : [],
      ),
    [isOwnRefereeList, rosterEntries],
  );

  // A parent subject is a two-step chain — the links, then the child documents
  // behind them. Before the links land there is nothing to resolve, so
  // `areChildrenResolved` alone would report "ready, no tournaments". The team
  // documents are the same kind of allow-list input: matched before they land,
  // every tournament fails the division test and the list renders empty.
  const isListReady =
    (!refereeId || areRefereedTournsLoaded) &&
    (!isOwnRefereeList || isRosterLoaded) &&
    (!fieldManagerId || areManagedTournsLoaded) &&
    isSubjectResolved &&
    (subject?.role !== UsersRoles.PARENT || isParentsLoaded) &&
    areChildrenResolved &&
    areLookedUpTeamsResolved;

  const addCol = teamId && (userIsAdmin || userIsCoach);
  const isOrgList = !teamId && !userId && !fieldManagerId && !refereeId;
  const addTournament = isOrgList && userIsAdmin;
  // An assigner adds only the hidden events nobody registers for
  const addEvent = isOrgList && userIsRefereeAssigner;
  // Either makes one from an outside organizer's schedule file
  const addImport = isOrgList && userManagesReferees;

  const onPay = (tournament) => {
    openModal({
      header: 'Payment',
      body: <PaymentModalBody team={teamId} tournament={tournament} />,
    });
  };

  // derive filtered and enriched tournament data
  const tournamentsToShow = useMemo(() => {
    // Answering now would answer wrongly — the spinner below covers the wait
    if (!isListReady) return [];

    return tournaments
      .filter((tourn) => {
        // location filter
        if (location !== locations[0] && cityOf(tourn.location) !== location)
          return false;
        // sport filter
        if (sport !== sports[0] && !tourn.sports.includes(sport)) return false;
        // time filter (matching start or end month)
        if (
          time !== times[0] &&
          time !== tourn.startDate.format('MMMM YYYY') &&
          time !== tourn.endDate.format('MMMM YYYY')
        )
          return false;
        // player admission filter (single-team mode only)
        if (
          userIsPlayer &&
          teamId &&
          !(tourn.admittedTeamIds || []).includes(teamId)
        )
          return false;
        // aggregate (userId) mode: keep tournaments matching any of the
        // subject's teams by division and invited; players/parents also
        // require the team to be admitted
        if (userId) {
          // `admittedTeamIds` is already gated on `invited === true` by the
          // backend, so it is the stricter of the two lists, not a separate one
          const inviteList = subjectAdmittedOnly
            ? tourn.admittedTeamIds
            : tourn.invitedTeamIds;
          const matches = subjectTeamIds.some((tid) => {
            const team = teamsById.get(tid);
            if (
              !team ||
              !tourn.sports.includes(team.sport) ||
              !tourn.genders.includes(team.gender) ||
              !tourn.ages.includes(team.age)
            )
              return false;
            return (inviteList || []).includes(tid);
          });
          if (!matches) return false;
        }
        // field manager filter
        if (fieldManagerId && !managedTournIds.has(tourn.id)) return false;
        // referee filter — assigned to a game, or accepted onto the roster
        if (
          refereeId &&
          !refereedTournIds.has(tourn.id) &&
          !acceptedTournIds.has(tourn.id)
        )
          return false;
        // division match filter
        if (teamId) {
          const team = teamsById.get(teamId);
          if (
            team &&
            (!tourn.sports.includes(team.sport) ||
              !tourn.genders.includes(team.gender) ||
              !tourn.ages.includes(team.age))
          )
            return false;
        }
        // invite filter — a team only sees tournaments it was invited to
        if (teamId && !(tourn.invitedTeamIds || []).includes(teamId))
          return false;
        return true;
      })
      .sort((a, b) => a.startDate.valueOf() - b.startDate.valueOf())
      .map((tourn) => {
        const {
          id,
          imgThumbUrl,
          name,
          location: tournLocation,
          sports: tournSports,
          genders,
          ages,
          startDate,
          endDate,
          fee,
          deposit,
          hidden,
        } = tourn;

        // compute payments and status. The backend stores only discounts above
        // zero and deletes the key when one is cleared, so absent means none
        const paid = paidByTournamentId.get(id) ?? 0;
        const discount = tourn.teamDiscounts?.[teamId] || 0;
        const remaining = fee - paid - discount;

        // derived display-friendly fields
        const formattedGenders = formatGenders(genders);
        const ageRange = formatAges(ages);
        const sportsList = tournSports.join(', ');
        return {
          id,
          imgThumbUrl,
          name,
          tournLocation,
          tournSports,
          formattedGenders,
          ageRange,
          sportsList,
          startDate,
          endDate,
          paid,
          discount,
          remaining,
          deposit,
          hidden,
        };
      });
  }, [
    tournaments,
    isListReady,
    location,
    sport,
    time,
    userIsPlayer,
    teamId,
    userId,
    subjectTeamIds,
    subjectAdmittedOnly,
    fieldManagerId,
    refereeId,
    managedTournIds,
    refereedTournIds,
    acceptedTournIds,
    paidByTournamentId,
    locations,
    sports,
    teamsById,
  ]);

  const searchValues = useMemo(
    () => tournamentsToShow.map((tourn) => [tourn.name, tourn.tournLocation]),
    [tournamentsToShow],
  );

  const displayedTournaments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tournamentsToShow;
    return tournamentsToShow.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [tournamentsToShow, searchValues, searchQuery]);

  return (
    <div className={'tournaments-list' + (addCol ? ' add-col' : '')}>
      <div className='sticky-top'>
        <div className='controls'>
          <ul className='filters'>
            <li>
              <FilterInput
                className='search'
                placeholder='Search'
                value={searchQuery}
                onChange={setSearchQuery}
                small
              />
            </li>
            <li>
              <Select
                options={locations}
                value={location}
                onChange={setLocation}
                placeholder='Location'
                small
                className='location'
              />
            </li>
            <li>
              <Select
                options={sports}
                value={sport}
                onChange={setSport}
                placeholder='Sport'
                small
                className='sport'
              />
            </li>
            <li>
              <Select
                options={times}
                value={time}
                onChange={setTime}
                placeholder='Time'
                small
                className='time'
              />
            </li>
          </ul>
          {(addTournament || addEvent || addImport) && (
            <div className='actions'>
              {addTournament && (
                <Link
                  to='/tournaments/new?edit=true'
                  className='button small bw'
                >
                  Add tournament
                </Link>
              )}
              {addEvent && (
                <Link
                  to='/tournaments/new?edit=true'
                  className='button small bw'
                >
                  New event
                </Link>
              )}
              {addImport && (
                <Link to='/tournaments/new/import' className='button small bw'>
                  Import schedule
                </Link>
              )}
            </div>
          )}
        </div>
        {!(searchQuery.trim() && displayedTournaments.length === 0) && (
          <ul className='header tab-header'>
            <li className='col-1' />
            <li className='col-23'>Tournament, Dates, Location</li>
            <li className='col-4'>Sport</li>
            <li className='col-5'>Division</li>
            <li className='col-45'>Division</li>
            {addCol && <li className='col-7'>Payment</li>}
          </ul>
        )}
      </div>
      {searchQuery.trim() && displayedTournaments.length === 0 && (
        <h5 className='name'>No results found for your search</h5>
      )}
      {displayedTournaments.map((tourn) => {
        const {
          id,
          imgThumbUrl,
          name,
          tournLocation,
          startDate,
          endDate,
          tournSports,
          formattedGenders,
          ageRange,
          sportsList,
          paid,
          discount,
          remaining,
          deposit,
          hidden,
        } = tourn;

        return (
          <Link className={'tournament'} to={'/tournaments/' + id} key={id}>
            <div
              className='col-1 bg-image wh-64'
              style={{
                backgroundImage: `url(${imgThumbUrl || placeholderTournament})`,
              }}
            />
            <div className='col-23'>
              <div className='img-name-wrap'>
                <div
                  className='bg-image wh-32'
                  style={{
                    backgroundImage: `url(${imgThumbUrl || placeholderTournament})`,
                  }}
                />
                <p className='upper font-medium'>{name}</p>
                {hidden && userManagesReferees && (
                  <span className='badge hidden-badge'>Hidden</span>
                )}
              </div>
              <DateRangeBadge start={startDate} end={endDate} />
              <p className='location'>
                <span className='fa-location gray-800' /> {tournLocation}
              </p>
            </div>
            <div className='col-4'>
              {tournSports.map((s) => (
                <p key={s}>{s}</p>
              ))}
            </div>
            <div className='col-5'>
              <p>{formattedGenders}</p>
              <p>{ageRange}</p>
            </div>
            <div className='col-45'>
              <p>{sportsList}</p>
              <p>{formattedGenders}</p>
              <p>{ageRange}</p>
            </div>
            {addCol && (
              <div className='col-7'>
                <PaymentInfo
                  paid={paid}
                  discount={discount}
                  remaining={remaining}
                  deposit={deposit}
                >
                  {userIsCoach && remaining > 0 && (
                    <button
                      className='small bw'
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onPay(id);
                      }}
                    >
                      Pay
                    </button>
                  )}
                </PaymentInfo>
              </div>
            )}
          </Link>
        );
      })}
      <Spinner show={!isListReady} />
    </div>
  );
}
